import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { doc, getDoc, updateDoc, collection, getDocs, query, where, deleteField } from 'firebase/firestore';
import { loadLinkedAthletes } from '../utils/athleteLinks';
import { membershipStatus } from '../utils/membership';
import { getRoleChipsConfig } from '../utils/memberChips';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import CompanyPanel from '../components/CompanyPanel';
import { resolveMember } from '../utils/resolveMember';
import { isStaffRoles } from '../utils/profileAccess';
import {
  Box, Typography, Paper, Grid, Chip, CircularProgress,
  Avatar, Button, TextField, Alert, Divider,
  FormControlLabel, Checkbox, MenuItem, Select, InputLabel, FormControl,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import EmailIcon from '@mui/icons-material/Email';

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

const ROLE_COLORS = {
  admin: { bg: '#FDEDEC', color: '#922B21' },
  coach: { bg: '#EBF5FB', color: '#1A5276' },
  assistant_coach: { bg: '#EBF5FB', color: '#1A5276' },
  volunteer: { bg: '#F4F6F7', color: '#555' },
  parent: { bg: '#E9F7EF', color: '#1E6B3C' },
  adult_athlete: { bg: '#FEF9E7', color: '#7B4800' },
  junior_athlete: { bg: '#F5EEF8', color: '#6C3483' },
  lead: { bg: '#FEF9E7', color: '#7B4800' },
  tournament_admin: { bg: '#EAF4FB', color: '#1A5276' },
  player: { bg: '#EBF5FB', color: '#1A5276' },
  renter: { bg: '#E0F2F1', color: '#00695C' },
  member_manager: { bg: '#FEF5E7', color: '#935116' },
  court_admin: { bg: '#E8F8F5', color: '#117864' },
};

const ROLE_LABELS = {
  admin: 'Admin', coach: 'Coach', assistant_coach: 'Assistant Coach',
  volunteer: 'Volunteer', parent: 'Parent', adult_athlete: 'Adult Athlete',
  junior_athlete: 'Junior Athlete', lead: 'Lead', tournament_admin: 'Tournament Admin',
  player: 'Player', member: 'Member', renter: 'Renter',
  member_manager: 'Member Manager', court_admin: 'Calendar Manager', hs_coach: 'HS Coach',
};

// Which high school an HS Coach coaches, as the school's code in
// settings/schoolSchedule (ERHS, TCHS…). Stored on the user as hsSchoolCode.
function HsSchoolPicker({ value, onChange }) {
  const [schools, setSchools] = useState([]);
  useEffect(() => {
    getDoc(doc(db, 'settings', 'schoolSchedule'))
      .then(s => setSchools(s.exists() ? s.data().schools || [] : [])).catch(() => {});
  }, []);
  return (
    <FormControl size="small" sx={{ mt: 1.5, minWidth: 260 }}>
      <InputLabel>Coaches at</InputLabel>
      <Select label="Coaches at" value={value || ''} onChange={e => onChange(e.target.value)}>
        <MenuItem value="">— Not set —</MenuItem>
        {schools.map(s => <MenuItem key={s.code} value={s.code}>{s.name} ({s.code})</MenuItem>)}
      </Select>
    </FormControl>
  );
}

const INDOOR_LEVELS = ['None', 'Recreational', 'Middle School', 'JV', 'Varsity', 'Collegiate', 'Professional'];
const BEACH_LEVELS = ['None', 'Beginner', 'Intermediate', 'Advanced', 'Elite'];

// Membership status vocabulary: active | trialing | frozen | past_due | canceled.
// trialing = prepaid (full access); frozen = paused.
const MEMBER_STATUS_COLORS = {
  active: { bg: '#E9F7EF', color: '#1E6B3C' },
  trialing: { bg: '#EBF5FB', color: '#1A5276' },
  frozen: { bg: '#EAECEE', color: '#566573' },
  past_due: { bg: '#FDEBD0', color: '#7B4800' },
  canceled: { bg: '#FDEDEC', color: '#922B21' },
  expired: { bg: '#FDEDEC', color: '#922B21' },
};

// Mirrors AthleteProfile so the two profiles read identically.
const STATUS_COLORS = {
  paid: { bg: '#E9F7EF', color: '#1E6B3C' },
  pending: { bg: '#FEF9E7', color: '#7B4800' },
  refunded: { bg: '#F5EEF8', color: '#6C3483' },
  partial_refund: { bg: '#FDEBD0', color: '#7B4800' },
  failed: { bg: '#FDEDEC', color: '#922B21' },
};

const InfoRow = ({ label, value }) => (
  <Grid size={{ xs: 12, sm: 6 }}>
    <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>{label}</Typography>
    <Typography variant="body1" sx={{ fontWeight: 500 }}>{value || '—'}</Typography>
  </Grid>
);

const formatDate = (val) => {
  if (!val) return '—';
  if (val.seconds) return new Date(val.seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (val.toDate) return val.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (typeof val === 'string') {
    const [y, m, d] = val.split('-');
    if (y && m && d) return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    const parsed = new Date(val);
    return isNaN(parsed) ? '—' : parsed.toLocaleDateString();
  }
  return '—';
};

// A child's monthly plan, as the parent should read it. The cancellation
// wording matches AthleteProfile: a plan cancelled at period end still runs to
// its end date, and the sessions left after it don't expire.
function childPlanStatus(a, planName) {
  const name = planName || 'Monthly plan';
  const end = formatDate(a.subscriptionCancelAt || a.subscriptionCurrentPeriodEnd);
  if (a.subscriptionStatus === 'active' && a.subscriptionCancelAtPeriodEnd) {
    return { label: `Cancelled · ends ${end}`, bg: '#FDEBD0', color: '#7B4800',
      note: `${name} — no further charges. Sessions left stay and don't expire.` };
  }
  if (a.subscriptionStatus === 'active') {
    return { label: `Active · renews ${end}`, bg: '#E9F7EF', color: '#1E6B3C', note: name };
  }
  if (a.subscriptionStatus === 'past_due') {
    return { label: 'Payment past due', bg: '#FDEDEC', color: '#922B21', note: name };
  }
  if (a.subscriptionStatus === 'canceled') {
    return { label: `Ended ${formatDate(a.subscriptionCanceledAt)}`, bg: '#EAECEE', color: '#566573',
      note: "Monthly plan ended. Sessions left don't expire." };
  }
  return { label: 'No monthly plan', bg: '#f0f0f0', color: '#555', note: '' };
}

const childName = (a) => `${a.firstName || ''} ${a.lastName || ''}`.trim() || 'Athlete';

function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userData, setUserData] = useState(null);
  const [linkedAthletes, setLinkedAthletes] = useState([]);
  const [athleteData, setAthleteData] = useState(null);
  // initial state from URL: ?edit=true (sent by People.js kebab → Edit action)
  const [editing, setEditing] = useState(searchParams.get('edit') === 'true');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [editError, setEditError] = useState('');
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  // Set when the viewer is neither staff nor the account owner.
  const [accessDenied, setAccessDenied] = useState(false);
  const [tournamentHistory, setTournamentHistory] = useState([]);
  const [tournamentHistoryLoading, setTournamentHistoryLoading] = useState(true);
  // Session + purchase history, so an adult profile carries the same tabs a
  // junior does. Athlete-keyed records need the linked athlete doc; membership
  // payments are account-level and exist even without one.
  const [allTiers, setAllTiers] = useState([]);
  const [tierSaving, setTierSaving] = useState(false);
  const [tierExpiry, setTierExpiry] = useState('');
  // Court-rental rates. Only used when the chosen tier is a rental one, where
  // the rate — not the tier — is what prices their bookings.
  const [rentalRates, setRentalRates] = useState([]);
  const [renterRateId, setRenterRateId] = useState('');
  // Customer records this account rents courts under. Usually one; two is legal
  // (internal customers may share a staff account), so this is a list.
  const [companyCustomers, setCompanyCustomers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [purchases, setPurchases] = useState([]);
  // Each linked child's check-ins and purchases, so a parent's profile shows
  // the family's history rather than only the parent's own (usually none).
  // { [athleteId]: { attendance, purchases, planName } }
  const [childHistory, setChildHistory] = useState({});
  const [userPurchases, setUserPurchases] = useState([]);

  // Tab + coach state
  const [tab, setTab] = useState(0);
  // ?tab=sessions — sent by People → Customers, which lands on a renter's
  // court time and billing rather than making you find it. Held as a key, not
  // an index, because the tab list is built at render time and differs by role.
  // Cleared the moment the user picks a tab themselves.
  const [pendingTabKey, setPendingTabKey] = useState(searchParams.get('tab') || '');
  const [recentClasses, setRecentClasses] = useState([]);
  const [classAttendees, setClassAttendees] = useState({});
  const [inviteSending, setInviteSending] = useState(false);
  // Admin: edit session balance via dialog (mirrors AthleteProfile pattern)
  const [balanceDialog, setBalanceDialog] = useState(false);
  const [newBalance, setNewBalance] = useState('');
  const [savingBalance, setSavingBalance] = useState(false);
  const [clinicDialog, setClinicDialog] = useState(false);
  const [newClinic, setNewClinic] = useState('');
  const [savingClinic, setSavingClinic] = useState(false);

  // An edit made from the Company tab lands back here, so the tab stops
  // showing the values it was opened with. The company name has a second home
  // on the user doc — the field on Contact Info — so renaming the customer
  // renames it there too, the same sync the profile save does in reverse.
  const applyCustomerSave = (saved) => {
    setCompanyCustomers(prev => prev.map(c => (c.id === saved.id ? { ...c, ...saved } : c)));
    const name = (saved.organizationName || '').trim();
    if (userData && saved.userId === userData.id && name !== (userData.organizationName || '')) {
      updateDoc(doc(db, 'users', userData.id), { organizationName: name })
        .then(() => {
          setUserData(prev => (prev ? { ...prev, organizationName: name } : prev));
          setForm(prev => ({ ...prev, organizationName: name }));
        })
        .catch(() => { /* non-fatal — the customer record already saved */ });
    }
  };

  const handleBalanceUpdate = async () => {
    if (!athleteData?.id) return;
    const bal = parseInt(newBalance);
    if (isNaN(bal)) { setEditError('Please enter a valid session balance.'); return; }
    setSavingBalance(true);
    try {
      await updateDoc(doc(db, 'athletes', athleteData.id), { sessionBalance: bal });
      setAthleteData(prev => prev ? { ...prev, sessionBalance: bal } : prev);
      setBalanceDialog(false);
      setNewBalance('');
      setSuccess('Session balance updated.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setEditError('Failed to update balance.');
    } finally {
      setSavingBalance(false);
    }
  };
  const [inviteSuccess, setInviteSuccess] = useState('');

  useEffect(() => {
    getDocs(collection(db, 'membershipTiers'))
      .then(s2 => setAllTiers(s2.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))))
      .catch(() => {});
    getDocs(collection(db, 'rentalRates'))
      .then(s2 => setRentalRates(s2.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.rateName || '').localeCompare(b.rateName || ''))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const currentUser = auth.currentUser;
      let admin = false;
      if (currentUser) {
        const adminDoc = await getDoc(doc(db, 'users', currentUser.uid));
        admin = adminDoc.exists() && (adminDoc.data().roles || []).includes('admin');
        if (admin) setIsAdmin(true);
      }
      // Try by document ID first
      const directDoc = await getDoc(doc(db, 'users', userId));
      let data = null;
      let docId = userId;

      if (directDoc.exists()) {
        data = directDoc.data();
      } else {
        // Try querying by uid field
        const snap = await getDocs(query(collection(db, 'users'), where('uid', '==', userId)));
        if (!snap.empty) {
          data = snap.docs[0].data();
          docId = snap.docs[0].id;
        }
      }

      if (data) {
        // Staff and the account owner only. The route only required a login,
        // so any member with another member's link could read their contact
        // details, purchases and sessions. Roles come from the canonical
        // account, so a staff member signed in on a merged login still counts;
        // owner is the signed-in login itself or the account it resolves to.
        // A screen, not a security boundary — the users read rule is still
        // any signed-in account.
        const viewer = currentUser ? await resolveMember(db, currentUser) : null;
        const viewerRoles = viewer?.data?.roles || [];
        const viewerIsStaff = admin || isStaffRoles(viewerRoles);
        const viewerIsOwner = !!currentUser
          && (currentUser.uid === docId || currentUser.uid === data.uid || viewer?.uid === docId);
        if (!viewerIsStaff && !viewerIsOwner) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }
        setUserData({ id: docId, ...data });

        // Fetch linked athletes — try docId first, fall back to data.uid.
        // The helper queries both parentUserId AND parentUserIds array.
        let athletes = await loadLinkedAthletes(db, docId);
        if (athletes.length === 0 && data.uid && data.uid !== docId) {
          athletes = await loadLinkedAthletes(db, data.uid);
        }
        setLinkedAthletes(athletes);
        try {
          const kids = athletes.filter(a => !a.isArchived && !a.mergedInto);
          const entries = await Promise.all(kids.map(async (a) => {
            const [aSnap, pSnap, planSnap] = await Promise.all([
              getDocs(query(collection(db, 'attendance'), where('athleteId', '==', a.id))),
              getDocs(query(collection(db, 'purchases'), where('athleteId', '==', a.id))),
              a.activePlanId ? getDoc(doc(db, 'plans', a.activePlanId)).catch(() => null) : Promise.resolve(null),
            ]);
            const ms = (v) => v?.toMillis?.() ?? (v?.seconds ? v.seconds * 1000 : 0);
            return [a.id, {
              attendance: aSnap.docs.map(d => ({ id: d.id, ...d.data() }))
                .sort((x, y) => (y.date || '').localeCompare(x.date || '')),
              purchases: pSnap.docs.map(d => ({ id: d.id, ...d.data() }))
                .sort((x, y) => (ms(y.purchaseDate) || ms(y.createdAt)) - (ms(x.purchaseDate) || ms(x.createdAt))),
              planName: planSnap && planSnap.exists() ? (planSnap.data().planName || '') : '',
            }];
          }));
          setChildHistory(Object.fromEntries(entries));
        } catch (e) {
          console.error('Failed to load children history:', e);
        }

        // Fetch own adult athlete record.
        //
        // By email OR by athletes.userId. Email alone missed any adult whose
        // athlete record carries no email — Mindy Vuong's has none — and this
        // lookup is what decides whether a name correction reaches the athlete
        // doc at all, so a miss here is a rename that silently only half lands.
        // userId is the stronger link of the two: it names the account outright
        // rather than inferring it from a shared address.
        {
          const [byEmail, byUserId] = await Promise.all([
            data.email
              ? getDocs(query(collection(db, 'athletes'), where('email', '==', data.email)))
              : Promise.resolve({ docs: [] }),
            getDocs(query(collection(db, 'athletes'), where('userId', '==', docId))),
          ]);
          const candidates = [...byUserId.docs, ...byEmail.docs];
          const ownAthlete = candidates.find(d => d.data().athleteType?.toLowerCase() === 'adult');
          if (ownAthlete) {
            const ad = ownAthlete.data();
            setAthleteData({ id: ownAthlete.id, ...ad });
            setForm(prev => ({
              ...prev,
              indoorExperience: ad.indoorExperience || '',
              beachExperience: ad.beachExperience || '',
            }));
            try {
              const [pSnap, aSnap] = await Promise.all([
                getDocs(query(collection(db, 'purchases'), where('athleteId', '==', ownAthlete.id))),
                getDocs(query(collection(db, 'attendance'), where('athleteId', '==', ownAthlete.id))),
              ]);
              setPurchases(pSnap.docs.map(d => ({ id: d.id, ...d.data() }))
                .sort((a, b) => (b.purchaseDate?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0)
                  - (a.purchaseDate?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0)));
              setAttendance(aSnap.docs.map(d => ({ id: d.id, ...d.data() }))
                .sort((a, b) => (b.date || '').localeCompare(a.date || '')));
            } catch (e) {
              console.error('Failed to load athlete history:', e);
            }
          }
        }

        // Membership payments hang off the user, not the athlete, so they load
        // for every account — including ones with no athlete record at all.
        // The Stripe backfill wrote 686 invoices with userId:null, keyed only by
        // stripeCustomerId/customerEmail, so a userId-only lookup misses every
        // migrated member's payment history. Match all three and dedupe by id.
        try {
          const custId = data.membership?.stripeCustomerId;
          const email = (data.email || '').trim().toLowerCase();
          const queries = [
            getDocs(query(collection(db, 'purchases'), where('userId', '==', docId))),
            ...(data.uid && data.uid !== docId
              ? [getDocs(query(collection(db, 'purchases'), where('userId', '==', data.uid)))] : []),
            ...(custId
              ? [getDocs(query(collection(db, 'purchases'), where('stripeCustomerId', '==', custId)))] : []),
            ...(email
              ? [getDocs(query(collection(db, 'purchases'), where('customerEmail', '==', email)))] : []),
          ];
          const snaps = await Promise.all(queries);
          const byId = new Map();
          snaps.forEach(snap => snap.docs.forEach(d => byId.set(d.id, { id: d.id, ...d.data() })));
          setUserPurchases([...byId.values()]);
        } catch (e) {
          console.error('Failed to load membership payments:', e);
        }
        // Court-rental customer records, which is where the company side of a
        // renter lives — bookings, invoices, deposit. Linked by userId when the
        // account was matched, by email when it was only ever a billing
        // contact. Admin-only: the customers collection is admin-read, so for
        // anyone else this stays empty and the Company tab never appears.
        if (admin) {
          try {
            const email = (data.email || '').toLowerCase().trim();
            const uids = [docId, ...(data.uid && data.uid !== docId ? [data.uid] : [])];
            const snaps = await Promise.all([
              ...uids.map(u => getDocs(query(collection(db, 'customers'), where('userId', '==', u)))),
              // email is stored as typed on a hand-added customer and lowercased
              // on one provisioned from an invite — match both fields.
              ...(email ? [
                getDocs(query(collection(db, 'customers'), where('email', '==', email))),
                getDocs(query(collection(db, 'customers'), where('userEmail', '==', email))),
              ] : []),
            ]);
            const byId = new Map();
            snaps.forEach(snap => snap.docs.forEach(d => byId.set(d.id, { id: d.id, ...d.data() })));
            setCompanyCustomers([...byId.values()]
              .sort((a, b) => (a.organizationName || '').localeCompare(b.organizationName || '')));
          } catch (e) {
            console.error('Failed to load customer records:', e);
          }
        }

        setForm({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          billingEmail: data.billingEmail || '',
          phone: data.phone || '',
          // Renters register with a business/group name; it lived only on the
          // user doc and the Customers tab, never on the profile itself.
          organizationName: data.organizationName || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          zip: data.zip || '',
          notes: data.notes || '',
          roles: data.roles || [],
          isVolunteer: (data.roles || []).includes('volunteer'),
          headRatePerHour: data.headRatePerHour || 0,
          assistantRatePerHour: data.assistantRatePerHour || 0,
          venmoUsername: data.venmoUsername || '',
          indoorExperience: '',
          beachExperience: '',
          // Parent / Guardian 2 — stored on the user's own doc.
          // Prefer guardian2* (newer schema) → fall back to legacy p2*.
          guardian2FirstName: data.guardian2FirstName || data.p2FirstName || '',
          guardian2LastName: data.guardian2LastName || data.p2LastName || '',
          guardian2Email: data.guardian2Email || data.p2Email || '',
          guardian2Phone: data.guardian2Phone || data.p2Phone || '',
        });

        // Coach data — fetch recent classes if user has coach/assistant_coach role
        const hasCoachRole = (data.roles || []).some(r => ['coach', 'assistant_coach'].includes(r));
        if (hasCoachRole) {
          try {
            const [headSnap, assistSnap] = await Promise.all([
              getDocs(query(collection(db, 'classes'), where('headCoachId', '==', docId))),
              getDocs(query(collection(db, 'classes'), where('assistantCoachId', '==', docId))),
            ]);
            const all = [
              ...headSnap.docs.map(d => ({ id: d.id, role: 'Head Coach', ...d.data() })),
              ...assistSnap.docs.map(d => ({ id: d.id, role: 'Assistant', ...d.data() })),
            ];
            all.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
            const recent = all.slice(0, 20);
            setRecentClasses(recent);
            // Attendee counts via 'in' chunks — one query per 30 classes rather
            // than one per class. Reserved and wait-listed rows are intentions,
            // not attendance.
            try {
              const ids = recent.map(c => c.id);
              const counts = {};
              for (let i = 0; i < ids.length; i += 30) {
                const attSnap = await getDocs(query(
                  collection(db, 'attendance'), where('classId', 'in', ids.slice(i, i + 30))));
                attSnap.docs.forEach(dd => {
                  const r = dd.data();
                  if (r.status === 'reserved' || r.status === 'waitlisted') return;
                  counts[r.classId] = (counts[r.classId] || 0) + 1;
                });
              }
              setClassAttendees(counts);
            } catch (e) { console.warn('Attendee counts failed:', e); }
          } catch (e) {
            console.error('Failed to load coach classes:', e);
          }
        }
      }
      setLoading(false);

      // Load tournament history — match against user_ids in standings
      // userId param could be the doc ID OR a uid field, so include both.
      const candidateIds = [docId, userId, data?.uid].filter(Boolean);
      try {
        const resultsSnap = await getDocs(collection(db, 'tournamentResults'));
        const matches = [];
        for (const d of resultsSnap.docs) {
          const trData = d.data();
          const tournamentId = trData.tournament_id || d.id;
          const tournamentName = trData.tournament_name || '';
          const divisions = trData.divisions || {};
          for (const [divName, divObj] of Object.entries(divisions)) {
            for (const standing of (divObj.standings || [])) {
              const userIds = standing.user_ids || [];
              const hit = candidateIds.some(id => userIds.includes(id));
              if (hit) {
                // Determine partner name from matched_players
                const partner = (standing.matched_players || []).find(p =>
                  p.user_id && !candidateIds.includes(p.user_id)
                );
                matches.push({
                  tournament_id: tournamentId,
                  tournament_name: tournamentName,
                  date: divObj.date || '',
                  division: divName,
                  team_name: standing.team_name,
                  partner_name: partner ? `${partner.first_name || ''} ${partner.last_name || ''}`.trim() : '',
                  place: standing.place,
                  rank: standing.rank,
                  wins: standing.wins,
                  losses: standing.losses,
                  earned_points: standing.earned_points || 0,
                });
              }
            }
          }
        }
        matches.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        setTournamentHistory(matches);
      } catch (err) {
        console.error('Failed to load tournament history:', err);
      }
      setTournamentHistoryLoading(false);
    };
    fetchData();
  }, [userId]);

  // Assign or clear a membership tier on this account.
  //
  // AthleteProfile's equivalent omits `status`, which leaves the person holding
  // a tier that grants nothing: isMemberInGoodStanding() accepts only 'active'
  // or 'trialing'. Assignment here always writes a status.
  //
  // pricingMode matters too — membershipStatus() reads a one-time membership as
  // expired unless it carries an expiresAt, so a one-time tier needs a date.
  // A court-rental tier is assigned server-side: it has to set the rate and
  // pair the account with a customer record, which is what the invite path does
  // and what assigning the tier alone used to skip. Everything else is a plain
  // local write, unchanged.
  const assignCourtRenterTier = async (tierId, rateIdOverride, billingOverride) => {
    setTierSaving(true); setEditError('');
    try {
      const fn = httpsCallable(getFunctions(), 'assignCourtRenter');
      const rateId = rateIdOverride !== undefined ? rateIdOverride : (renterRateId || '');
      const res = await fn({ uid: userData.id, tierId, rentalRateId: rateId,
        ...(billingOverride !== undefined ? { courtBilling: billingOverride } : {}) });
      const tier = allTiers.find(t => t.id === tierId);
      setUserData(u => ({
        ...u,
        membership: {
          ...(u.membership || {}),
          tierId, tierName: tier?.name || '', pricingMode: 'court_rental',
          status: 'active', source: 'admin_assigned',
          rentalRateId: rateId,
          rentalRateName: res.data?.rateName || '',
          courtBilling: res.data?.courtBilling || 'monthly',
        },
      }));
      setSuccess(`${res.data?.tierName || 'Tier'} assigned`
        + (res.data?.rateName ? ` on the ${res.data.rateName} rate.` : ' — set a rate so their bookings price correctly.'));
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) {
      setEditError(`Failed to assign rental tier: ${e.message}`);
    } finally {
      setTierSaving(false);
    }
  };

  const assignTier = async (tierId) => {
    if (!isAdmin || !userData) return;
    const chosen = allTiers.find(t => t.id === tierId);
    if (tierId && chosen?.pricingMode === 'court_rental') {
      return assignCourtRenterTier(tierId);
    }
    setTierSaving(true); setEditError('');
    try {
      if (!tierId) {
        await updateDoc(doc(db, 'users', userData.id), { membership: deleteField() });
        setUserData(u => ({ ...u, membership: null }));
        setSuccess('Membership removed.');
      } else {
        const tier = allTiers.find(t => t.id === tierId);
        const oneTime = tier?.pricingMode === 'one_time' || tier?.isDayPass === true;
        if (oneTime && !tierExpiry) {
          setEditError('This is a one-time tier — set an access-through date, or it will read as expired immediately.');
          setTierSaving(false); return;
        }
        const membership = {
          ...(userData.membership || {}),
          tierId,
          tierName: tier?.name || '',
          pricingMode: tier?.pricingMode || 'recurring',
          ...(tier?.isDayPass ? { isDayPass: true } : {}),
          status: 'active',
          source: 'admin_assigned',
          weeklyRegularHours: Number(tier?.weeklyRegularHours) || 0,
          weeklyPrimeHours: Number(tier?.weeklyPrimeHours) || 0,
          ...(oneTime ? { expiresAt: new Date(`${tierExpiry}T23:59:59`) } : {}),
          assignedAt: new Date(),
          assignedBy: auth.currentUser?.uid || null,
        };
        await updateDoc(doc(db, 'users', userData.id), { membership });
        setUserData(u => ({ ...u, membership }));
        setSuccess(`${tier?.name || 'Tier'} assigned.`);
      }
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setEditError(`Failed to assign tier: ${e.message}`);
    } finally {
      setTierSaving(false);
    }
  };

  const update = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    setEditError('');
    try {
      const newRoles = form.roles !== undefined ? form.roles : (userData.roles || []);
      const { indoorExperience, beachExperience, isVolunteer, roles, email, ...userFields } = form;
      const updates = {
        ...userFields,
        // Normalize guardian2 email lowercase for Phase B matching
        guardian2Email: (userFields.guardian2Email || '').toLowerCase().trim(),
        // Lowercased so it matches the Stripe-charge lookups, which compare
        // against lowercased addresses. Blank means "same as login" — stored as
        // empty rather than a copy, so changing the login email doesn't leave a
        // stale billing address behind.
        billingEmail: (userFields.billingEmail || '').toLowerCase().trim(),
        roles: newRoles,
        headRatePerHour: parseFloat(userFields.headRatePerHour) || 0,
        assistantRatePerHour: parseFloat(userFields.assistantRatePerHour) || 0,
        // Clear legacy p2* fields if present
        p2FirstName: deleteField(),
        p2LastName: deleteField(),
        p2Email: deleteField(),
        p2Phone: deleteField(),
        p2Nickname: deleteField(),
        p2Address: deleteField(),
        p2City: deleteField(),
        p2State: deleteField(),
        p2Zip: deleteField(),
        p2Relationship: deleteField(),
        updatedAt: new Date(),
      };
      await updateDoc(doc(db, 'users', userData.id), updates);
      // The company name has a second home on the customer doc, which is what
      // the Customers tab lists them under. Keep the two in step, or renaming
      // here would leave that tab showing the old name. Admin-only write, so
      // a self-edit just skips it.
      if (isAdmin && (userFields.organizationName || '') !== (userData.organizationName || '')) {
        try {
          const custSnap = await getDocs(query(collection(db, 'customers'), where('userId', '==', userData.id)));
          await Promise.all(custSnap.docs.map(c =>
            updateDoc(doc(db, 'customers', c.id), { organizationName: (userFields.organizationName || '').trim() })));
        } catch (e) { /* non-fatal — the profile save already succeeded */ }
      }
      // Also update athlete doc if exists.
      //
      // The name travels with it. A person registering as an adult gets two
      // documents — a user and an athlete — written from the same two form
      // fields, and renaming here used to touch only the first. The Dashboard's
      // recent-activity feed reads the athlete doc, as do the People rosters, so
      // a corrected name went on showing its old self in every one of them.
      // Eddie Cherry was carrying his full name in BOTH name fields on the
      // athlete record for a day after the account had been put right.
      //
      // Same reasoning as the organizationName sync just above, which already
      // keeps the customers doc in step for exactly this reason.
      if (athleteData?.id) {
        const athleteUpdates = { indoorExperience, beachExperience };
        const first = (userFields.firstName || '').trim();
        const last = (userFields.lastName || '').trim();
        if (first && first !== (athleteData.firstName || '')) athleteUpdates.firstName = first;
        if (last && last !== (athleteData.lastName || '')) athleteUpdates.lastName = last;
        await updateDoc(doc(db, 'athletes', athleteData.id), athleteUpdates);
        setAthleteData(prev => ({ ...prev, ...athleteUpdates }));
      }
      // setUserData with non-sentinel values for local state — don't store deleteField sentinels
      const localUpdates = { ...userFields, guardian2Email: updates.guardian2Email, roles: newRoles, headRatePerHour: updates.headRatePerHour, assistantRatePerHour: updates.assistantRatePerHour };
      setUserData(prev => ({ ...prev, ...localUpdates, p2FirstName: '', p2LastName: '', p2Email: '', p2Phone: '' }));
      setEditing(false);
      setSuccess('Profile updated successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setEditError('Failed to save. Please try again.');
    }
    setSaving(false);
  };

  const handleSendInvite = async () => {
    if (!userData?.email) return;
    setInviteSending(true);
    setInviteSuccess('');
    try {
      const functions = getFunctions();
      const sendInvite = httpsCallable(functions, 'sendCoachInvite');
      await sendInvite({
        coachId: userData.id,
        coachEmail: userData.email,
        coachFirstName: userData.firstName,
      });
      setInviteSuccess('Invite sent to ' + userData.email);
      setTimeout(() => setInviteSuccess(''), 5000);
    } catch (err) {
      console.error('Invite failed:', err);
      setEditError('Failed to send invite.');
    }
    setInviteSending(false);
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (accessDenied) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2, color: '#1E3A5F' }}>Back</Button>
          <Typography variant="h6" sx={{ color: '#555' }}>This profile isn't available.</Typography>
          <Typography variant="body2" sx={{ color: '#888', mt: 1 }}>
            Profiles can only be viewed by club staff and the account's owner.
          </Typography>
        </Box>
      </Layout>
    );
  }

  if (!userData) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2, color: '#1E3A5F' }}>Back</Button>
          <Typography variant="h6" sx={{ color: '#888' }}>User not found.</Typography>
        </Box>
      </Layout>
    );
  }

  const initials = (userData.firstName?.charAt(0) || '') + (userData.lastName?.charAt(0) || '');


  // A court renter: on a court-rental tier, or the account behind a company's
  // customer record. Their profile is about court time and billing, not
  // training — no clinic credits, no athlete details — and the company
  // details live with their contact info rather than on a tab of their own.
  const isRenter = allTiers.find(t => t.id === userData.membership?.tierId)?.pricingMode === 'court_rental'
    || companyCustomers.length > 0;
  return (
    <Layout>
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ color: '#1E3A5F' }}>Back</Button>
          <Button
            variant="contained"
            startIcon={editing ? <SaveIcon /> : <EditIcon />}
            onClick={editing ? handleSave : () => setEditing(true)}
            disabled={saving}
            sx={{ backgroundColor: editing ? '#27AE60' : '#1E3A5F', '&:hover': { backgroundColor: editing ? '#1E8449' : '#2E6DA4' } }}
          >
            {editing ? (saving ? 'Saving...' : 'Save Changes') : 'Edit Profile'}
          </Button>
        </Box>
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}

        {/* Avatar Card */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar src={userData.photoURL || ''} sx={{ width: 72, height: 72, backgroundColor: '#1E3A5F', fontSize: '1.6rem' }}>
            {!userData.photoURL && (initials || userData.email?.charAt(0).toUpperCase())}
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>
              {userData.firstName} {userData.lastName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#888' }}>{userData.email}</Typography>
            {(() => {
              // Same rule as the People list: a tier badge replaces the generic
              // 'member' chip, so a Silver member reads SILVER here too.
              const { roles: chipRoles, tierStyle } = getRoleChipsConfig(userData);
              return (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {tierStyle && (
                    <Chip label={tierStyle.label} size="small"
                      sx={{ backgroundColor: tierStyle.bg, color: tierStyle.color, fontWeight: 700, fontSize: '0.75rem', letterSpacing: 0.5 }} />
                  )}
                  {chipRoles.map(role => (
                    <Chip key={role} label={ROLE_LABELS[role] || role} size="small"
                      sx={{ backgroundColor: ROLE_COLORS[role]?.bg || '#f0f0f0', color: ROLE_COLORS[role]?.color || '#555', fontWeight: 600, fontSize: '0.75rem' }} />
                  ))}
                </Box>
              );
            })()}
          </Box>
        </Paper>

        {/* Clinic Credits — independent of athlete linkage (tournament no-shows
            are credited here even with no athlete record) */}
        {!isRenter && (isAdmin || (userData.clinicCredits ?? 0) > 0) && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#8E44AD' }}>Clinic Credits</Typography>
              <Typography variant="body2" sx={{ color: '#888' }}>Redeemable toward a clinic</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: (userData.clinicCredits ?? 0) > 0 ? '#8E44AD' : '#bbb' }}>
                {userData.clinicCredits ?? 0}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                credit{(userData.clinicCredits ?? 0) === 1 ? '' : 's'}
              </Typography>
              {isAdmin && (
                <Button size="small" variant="outlined"
                  onClick={() => { setNewClinic(String(userData.clinicCredits ?? 0)); setClinicDialog(true); }}
                  sx={{ mt: 0.5, borderColor: '#8E44AD', color: '#8E44AD', fontSize: '0.7rem' }}>
                  Update
                </Button>
              )}
            </Box>
          </Paper>
        )}

        {/* Session Balance */}
        {athleteData && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>Session Balance</Typography>
              <Typography variant="body2" sx={{ color: '#888' }}>Current training sessions</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: (athleteData.sessionBalance ?? 0) > 2 ? '#27AE60' : (athleteData.sessionBalance ?? 0) > 0 ? '#E67E22' : '#E74C3C' }}>
                {athleteData.sessionBalance ?? 0}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>sessions remaining</Typography>
              {isAdmin && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => { setNewBalance(String(athleteData.sessionBalance ?? 0)); setBalanceDialog(true); }}
                  sx={{ mt: 0.5, borderColor: '#1E3A5F', color: '#1E3A5F', fontSize: '0.7rem' }}
                >
                  Update
                </Button>
              )}
            </Box>
          </Paper>
        )}

        {/* Membership belongs to the person, not to their athlete record. It
            sat inside Athlete Details, which meant assigning a court renter — who
            has no athlete record at all — meant opening a tab about athletes.
            It lives at page level now, beside the other admin cards. */}
        {isAdmin && (
          <Box sx={{ mb: 3, p: 2, borderRadius: 2, backgroundColor: '#f4f6f9', border: '1px solid #e0e4e8' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>
              Assign membership tier
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <TextField select size="small" label="Tier" disabled={tierSaving}
                value={userData.membership?.tierId || ''}
                onChange={e => assignTier(e.target.value || null)}
                sx={{ minWidth: 260, backgroundColor: 'white' }}
                helperText={userData.membership?.stripeSubscriptionId
                  ? 'Billed through Stripe — assigning here does not change that subscription'
                  : allTiers.find(t => t.id === userData.membership?.tierId)?.pricingMode === 'court_rental'
                    ? 'No subscription — court time is paid by card through Stripe when they reserve'
                    : 'Assigns directly, no Stripe involved'}>
                <MenuItem value="">— No membership —</MenuItem>
                {allTiers.map(t => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}{t.monthlyPrice ? ` — $${t.monthlyPrice}` : ' — $0'}
                  </MenuItem>
                ))}
              </TextField>
              <TextField size="small" type="date" label="Access through" disabled={tierSaving}
                value={tierExpiry} onChange={e => setTierExpiry(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ backgroundColor: 'white' }}
                helperText="Required for one-time tiers; leave blank for recurring" />
              {/* Only on a rental tier, and only there because the rate
                  is what prices a renter — the tier just lets them book.
                  Pick it before the tier: choosing the tier is what saves. */}
              {(() => {
                const cur = allTiers.find(t => t.id === userData.membership?.tierId);
                const anyRental = allTiers.some(t => t.pricingMode === 'court_rental');
                if (!anyRental) return null;
                const onRental = cur?.pricingMode === 'court_rental';
                return (
                  <TextField select size="small" label="Rental rate" disabled={tierSaving}
                    value={renterRateId || userData.membership?.rentalRateId || ''}
                    onChange={e => {
                      setRenterRateId(e.target.value);
                      // Already on a rental tier? Then this IS the change
                      // being made, and there is no tier pick coming to
                      // carry it — so save it now.
                      if (onRental) assignCourtRenterTier(cur.id, e.target.value);
                    }}
                    sx={{ minWidth: 240, backgroundColor: 'white' }}
                    helperText={onRental
                      ? 'Prices their court bookings — saves as soon as you pick'
                      : 'For court-rental tiers — pick this first, then the tier'}>
                    <MenuItem value="">— No rate —</MenuItem>
                    {rentalRates.map(r => {
                      const bits = [];
                      if (Number(r.hourlyRate) > 0) bits.push(`$${Number(r.hourlyRate)}`);
                      if (Number(r.primeRate) > 0) bits.push(`$${Number(r.primeRate)} prime`);
                      if (Number(r.weekendRate) > 0) bits.push(`$${Number(r.weekendRate)} wknd`);
                      return (
                        <MenuItem key={r.id} value={r.id}>
                          {r.rateName}{bits.length ? ` — ${bits.join(' / ')}` : ''}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                );
              })()}
              {/* When they pay, as against how much — the rate above is the
                  amount, this is the timing. Only on a rental tier, where court
                  time is the whole of what they are billed for. Saves on pick,
                  like the rate. */}
              {/* No choice to offer: a rental tier is pay-now whatever is stored
                  (paysCourtTimeUpFront on the server), so a Monthly option here
                  promised a billing mode the booking path never used. Said
                  plainly instead, with the rule that comes with it. */}
              {(() => {
                const cur = allTiers.find(t => t.id === userData.membership?.tierId);
                if (cur?.pricingMode !== 'court_rental') return null;
                return (
                  <Box sx={{ maxWidth: 320, pt: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#555', display: 'block', fontWeight: 600 }}>
                      Court billing: paid when they reserve
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                      Card charged through Stripe at checkout for every booking. Any unpaid
                      balance or deposit must be settled before they can reserve more time.
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
          </Box>
        )}

        {/* Tabs */}
        {(() => {
          const hasCoach = (userData.roles || []).some(r => ['coach', 'assistant_coach'].includes(r));
          const hasAthletes = linkedAthletes.length > 0;
          // The five core tabs match AthleteProfile exactly, so an adult and a
          // junior profile read the same. Coaching and Athletes are extra
          // context and only appear when the account has them.
          // Keyed rather than numbered: the old index arithmetic
          // (1 + hasCoach + hasAthletes) had to be recomputed by hand at every
          // panel, and silently mismatches the moment a tab is added.
          // A parent who does not play has nothing for Athlete Details to show
          // but its "no athlete record" line, while their children sat in the
          // last tab — scrolled out of sight on a narrow screen. For them the
          // children take Athlete Details' place. Anyone with their own
          // athlete record, the adult_athlete role, or a membership (which is
          // shown in Athlete Details) keeps the usual layout.
          const parentOnly = hasAthletes && !athleteData
            && !(userData.roles || []).includes('adult_athlete')
            && !userData.membership?.tierId;
          const tabs = [
            { key: 'contact', label: 'Contact Info' },
            ...(parentOnly
              ? [{ key: 'athletes', label: 'Linked Athletes' }]
              : isRenter ? [] : [{ key: 'details', label: 'Athlete Details' }]),
            { key: 'sessions', label: 'Sessions' },
            { key: 'purchases', label: 'Purchases' },
            { key: 'tournaments', label: 'Tournaments' },
            ...(hasCoach ? [{ key: 'coaching', label: 'Coaching' }] : []),
            ...(hasAthletes && !parentOnly ? [{ key: 'athletes', label: 'Linked Athletes' }] : []),
            // No Company tab: a renter's company details sit under Contact
            // Info, and their court time and billing under Sessions — the same
            // CompanyPanel the Customers tab shows, split across the two.
          ];
          // A tab can disappear (roles edited, links removed) while selected.
          const wantedIdx = pendingTabKey ? tabs.findIndex(t => t.key === pendingTabKey) : -1;
          const activeIdx = wantedIdx >= 0 ? wantedIdx : (tabs[tab] ? tab : 0);
          const at = tabs[activeIdx].key;
          return (
            <Paper elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
              <Tabs value={activeIdx} onChange={(_, v) => { setPendingTabKey(''); setTab(v); }} sx={{ borderBottom: '1px solid #eee', px: 2 }}
                variant="scrollable" scrollButtons="auto">
                {tabs.map(t => <Tab key={t.key} label={t.label} />)}
              </Tabs>
              <Box sx={{ p: 3 }}>

                {/* Contact Info */}
                <TabPanel value={at} index="contact">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Contact Information</Typography>
                  <Grid container spacing={2}>
                    {editing ? (
                      <>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField fullWidth label="First Name" value={form.firstName} onChange={e => update('firstName', e.target.value)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField fullWidth label="Last Name" value={form.lastName} onChange={e => update('lastName', e.target.value)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField fullWidth label="Email (login)" value={form.email} disabled helperText="Login email cannot be changed here" />
                        </Grid>
                        {/* Admin only, and enforced in firestore.rules rather
                            than here: absorbMiniAccount matches member shells on
                            this address, so a self-written one was a way to claim
                            somebody else's paid shell. */}
                        {isAdmin && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField fullWidth label="Billing email" type="email"
                              value={form.billingEmail || ''}
                              onChange={e => update('billingEmail', e.target.value)}
                              placeholder={form.email}
                              error={Boolean(form.billingEmail) && !/^\S+@\S+\.\S+$/.test(form.billingEmail)}
                              helperText={Boolean(form.billingEmail) && !/^\S+@\S+\.\S+$/.test(form.billingEmail)
                                ? 'Enter a valid email address'
                                : 'Where Stripe sends receipts. Blank = same as login.'} />
                          </Grid>
                        )}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField fullWidth label="Phone" value={form.phone} onChange={e => update('phone', e.target.value)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField fullWidth label="Company / organization"
                            value={form.organizationName || ''}
                            onChange={e => update('organizationName', e.target.value)}
                            helperText="Business or group they book courts as" />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <TextField fullWidth label="Street Address" value={form.address} onChange={e => update('address', e.target.value)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <TextField fullWidth label="City" value={form.city} onChange={e => update('city', e.target.value)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <TextField fullWidth label="State" value={form.state} onChange={e => update('state', e.target.value)} inputProps={{ maxLength: 2 }} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField fullWidth label="ZIP" value={form.zip} onChange={e => update('zip', e.target.value)} />
                        </Grid>

                        {/* Parent / Guardian 2 — only for users who have the parent role.
                            Adult athletes / coaches / admins without kids don't need this. */}
                        {(userData.roles || []).includes('parent') && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mt: 1, mb: 1 }}>
                                Parent / Guardian 2 (Optional)
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                                Adding a co-parent's email or phone lets them sign in and automatically link to the existing athletes.
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <TextField fullWidth label="First Name" value={form.guardian2FirstName} onChange={e => update('guardian2FirstName', e.target.value)} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <TextField fullWidth label="Last Name" value={form.guardian2LastName} onChange={e => update('guardian2LastName', e.target.value)} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <TextField fullWidth label="Email" value={form.guardian2Email} onChange={e => update('guardian2Email', e.target.value)} helperText="Used to auto-link when they sign in" />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <TextField fullWidth label="Phone" value={form.guardian2Phone} onChange={e => update('guardian2Phone', e.target.value)} />
                            </Grid>
                          </>
                        )}
                        {isAdmin && (
                          <Grid size={{ xs: 12 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>Roles</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {['admin','coach','assistant_coach','tournament_admin','volunteer','parent','player','lead','member_manager','court_admin','hs_coach'].map(role => {
                                const active = (form.roles || userData.roles || []).includes(role);
                                return (
                                  <Chip key={role}
                                    label={ROLE_LABELS[role] || role}
                                    onClick={() => {
                                      const current = form.roles || userData.roles || [];
                                      const next = active ? current.filter(r => r !== role) : [...current, role];
                                      update('roles', next);
                                    }}
                                    sx={{
                                      cursor: 'pointer',
                                      backgroundColor: active ? (ROLE_COLORS[role]?.bg || '#e0e0e0') : '#f5f5f5',
                                      color: active ? (ROLE_COLORS[role]?.color || '#333') : '#999',
                                      fontWeight: active ? 700 : 400,
                                      border: active ? `1px solid ${ROLE_COLORS[role]?.color || '#999'}` : '1px solid #ddd',
                                      '&:hover': { opacity: 0.85 },
                                    }}
                                  />
                                );
                              })}
                            </Box>
                            {(form.roles || userData.roles || []).includes('hs_coach') && (
                              <HsSchoolPicker value={form.hsSchoolCode ?? userData.hsSchoolCode}
                                onChange={v => update('hsSchoolCode', v)} />
                            )}
                          </Grid>
                        )}
                      </>
                    ) : (
                      <>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" sx={{ color: '#888' }}>Email</Typography>
                          <Typography variant="body1">{userData.email || '—'}</Typography>
                        </Grid>
                        {userData.liabilityInsuranceConfirmed !== undefined && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" sx={{ color: '#888' }}>Liability insurance</Typography>
                            <Typography variant="body1" sx={{ color: userData.liabilityInsuranceConfirmed ? '#1E6B3C' : '#922B21' }}>
                              {userData.liabilityInsuranceConfirmed ? 'Confirmed' : 'Not confirmed'}
                              {userData.liabilityInsuranceDate
                                ? ` · ${formatDate(userData.liabilityInsuranceDate)}` : ''}
                            </Typography>
                          </Grid>
                        )}
                        {userData.billingEmail && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" sx={{ color: '#888' }}>Billing email</Typography>
                            <Typography variant="body1">{userData.billingEmail}</Typography>
                          </Grid>
                        )}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" sx={{ color: '#888' }}>Phone</Typography>
                          <Typography variant="body1">{userData.phone || '—'}</Typography>
                        </Grid>
                        {/* Only renters have one — shown when set rather than an
                            empty row on every athlete and parent profile. */}
                        {userData.organizationName && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" sx={{ color: '#888' }}>Company</Typography>
                            <Typography variant="body1">{userData.organizationName}</Typography>
                          </Grid>
                        )}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="caption" sx={{ color: '#888' }}>Address</Typography>
                          <Typography variant="body1">
                            {userData.address ? `${userData.address}, ${userData.city}, ${userData.state} ${userData.zip}` : '—'}
                          </Typography>
                        </Grid>
                        {(userData.roles || []).includes('parent') && (userData.guardian2FirstName || userData.guardian2Email || userData.p2FirstName || userData.p2Email) && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mt: 1, mb: 1 }}>
                                Parent / Guardian 2
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <Typography variant="caption" sx={{ color: '#888' }}>Name</Typography>
                              <Typography variant="body1">
                                {`${userData.guardian2FirstName || userData.p2FirstName || ''} ${userData.guardian2LastName || userData.p2LastName || ''}`.trim() || '—'}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <Typography variant="caption" sx={{ color: '#888' }}>Email</Typography>
                              <Typography variant="body1">{userData.guardian2Email || userData.p2Email || '—'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <Typography variant="caption" sx={{ color: '#888' }}>Phone</Typography>
                              <Typography variant="body1">{userData.guardian2Phone || userData.p2Phone || '—'}</Typography>
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                  </Grid>

                  {/* Notes inside Profile tab */}
                  {(userData.notes || editing) && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>Notes</Typography>
                      {editing ? (
                        <TextField fullWidth multiline rows={3} value={form.notes} onChange={e => update('notes', e.target.value)} />
                      ) : (
                        <Typography variant="body2" sx={{ color: '#555' }}>{userData.notes}</Typography>
                      )}
                    </>
                  )}
                  {companyCustomers.map(c => (
                    <Box key={c.id}>
                      <Divider sx={{ my: 3 }} />
                      <CompanyPanel customer={c} onCustomerChange={applyCustomerSave} sections={['company']} />
                    </Box>
                  ))}
                </TabPanel>

                {/* Athlete Details — volleyball background and membership.
                    Empty for accounts with no athlete record (parents, admins);
                    the tab still shows so every profile has the same shape. */}
                <TabPanel value={at} index="details">
                  {userData.membership?.tierId && (() => {
                    const m = userData.membership;
                    const status = membershipStatus(userData) || m.status;
                    const chip = MEMBER_STATUS_COLORS[status] || { bg: '#f0f0f0', color: '#555' };
                    return (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Membership</Typography>
                        <Grid container spacing={2}>
                          <InfoRow label="Tier" value={m.tierName} />
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Status</Typography>
                            <Chip label={status} size="small"
                              sx={{ backgroundColor: chip.bg, color: chip.color, fontWeight: 600, textTransform: 'capitalize' }} />
                          </Grid>
                          <InfoRow label="Member Since" value={formatDate(m.startedAt)} />
                          <InfoRow
                            label={status === 'trialing' ? 'Prepaid Through' : 'Current Period Ends'}
                            value={formatDate(m.currentPeriodEnd)} />
                          <InfoRow label="Weekly Regular Hours"
                            value={m.weeklyRegularHours ? `${m.regularHoursUsed || 0} / ${m.weeklyRegularHours}` : 'Unlimited'} />
                          <InfoRow label="Weekly Prime Hours"
                            value={m.weeklyPrimeHours ? `${m.primeHoursUsed || 0} / ${m.weeklyPrimeHours}` : 'None included'} />
                          {m.migratedFrom && (
                            <Grid size={{ xs: 12 }}>
                              <Typography variant="caption" sx={{ color: '#888' }}>
                                Migrated from {m.migratedFrom}
                                {m.cancelAtPeriodEnd ? ' · cancels at period end' : ''}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                        <Divider sx={{ mt: 3 }} />
                      </Box>
                    );
                  })()}
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Athlete Details</Typography>
                  {!athleteData ? (
                    <Typography variant="body2" sx={{ color: '#aaa' }}>
                      No athlete record linked to this account.
                    </Typography>
                  ) : editing ? (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                          <InputLabel>Indoor Experience</InputLabel>
                          <Select value={form.indoorExperience} label="Indoor Experience"
                            onChange={e => update('indoorExperience', e.target.value)}>
                            {INDOOR_LEVELS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                          <InputLabel>Beach Experience</InputLabel>
                          <Select value={form.beachExperience} label="Beach Experience"
                            onChange={e => update('beachExperience', e.target.value)}>
                            {BEACH_LEVELS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      <InfoRow label="Athlete Type" value={athleteData.athleteType} />
                      <InfoRow label="Skill Group" value={athleteData.skillGroup} />
                      <InfoRow label="Indoor Experience" value={athleteData.indoorExperience} />
                      <InfoRow label="Beach Experience" value={athleteData.beachExperience} />
                      <InfoRow label="Plan" value={athleteData.planName} />
                      <InfoRow label="Waivers Signed" value={athleteData.waiverSigned ? 'Yes' : 'No'} />
                    </Grid>
                  )}
                </TabPanel>

                {/* Sessions */}
                <TabPanel value={at} index="sessions">
                  {/* A renter's sessions are their court rentals: the figures,
                      every booking (their own and the company's), and what
                      they have been billed. The renter's own bookings go on the
                      first company's panel only, so none is listed twice. */}
                  {isRenter ? (
                    (companyCustomers.length ? companyCustomers : [null]).map((c, i) => (
                      <Box key={c?.id || 'own'} sx={{ mt: i ? 4 : 0 }}>
                        {companyCustomers.length > 1 && (
                          <>
                            {i > 0 && <Divider sx={{ mb: 2 }} />}
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>
                              {c.organizationName}
                            </Typography>
                          </>
                        )}
                        <CompanyPanel customer={c} onCustomerChange={applyCustomerSave}
                          uid={i === 0 ? userId : null}
                          rentalRateId={i === 0 ? (userData.membership?.rentalRateId || null) : null}
                          sections={['stats', 'reservations', 'deposit', 'invoices']} />
                      </Box>
                    ))
                  ) : (<>
                  {(athleteData || attendance.length > 0 || linkedAthletes.length === 0) && (<>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Sessions</Typography>
                  {attendance.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#aaa' }}>No session history found.</Typography>
                  ) : (
                    <Box>
                      {attendance.map((a, i) => (
                        <Box key={a.id} sx={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          py: 1.5, px: 2, borderRadius: 2,
                          backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white', mb: 0.5
                        }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(a.date)}</Typography>
                            <Typography variant="caption" sx={{ color: '#888', textTransform: 'capitalize' }}>{a.sessionType || '—'}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" sx={{ color: '#888' }}>Balance after</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E3A5F' }}>{a.balanceAfter ?? '—'}</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                  </>)}
                  {linkedAthletes.filter(a => childHistory[a.id]).map((a, ci) => {
                    const h = childHistory[a.id];
                    const bal = a.sessionBalance || 0;
                    return (
                      <Box key={a.id} sx={{ mt: (ci || athleteData || attendance.length) ? 3 : 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F' }}>
                            {childName(a)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#888' }}>
                            {bal} session{bal === 1 ? '' : 's'} left · {h.attendance.length} attended
                          </Typography>
                        </Box>
                        {h.attendance.length === 0 ? (
                          <Typography variant="body2" sx={{ color: '#aaa' }}>No sessions yet.</Typography>
                        ) : h.attendance.map((r, i) => (
                          <Box key={r.id} sx={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            py: 1.5, px: 2, borderRadius: 2,
                            backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white', mb: 0.5
                          }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(r.date)}</Typography>
                              <Typography variant="caption" sx={{ color: '#888', textTransform: 'capitalize' }}>{r.sessionType || '—'}</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="caption" sx={{ color: '#888' }}>Balance after</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E3A5F' }}>{r.balanceAfter ?? '—'}</Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    );
                  })}
                  </>)}
                </TabPanel>

                {/* Purchases */}
                <TabPanel value={at} index="purchases">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Purchases</Typography>
                  {purchases.length === 0 ? (
                    (athleteData || linkedAthletes.length === 0) && (
                      <Typography variant="body2" sx={{ color: '#aaa' }}>No purchases recorded yet.</Typography>
                    )
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#1E3A5F' }}>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Plan</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Sessions</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Amount</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {purchases.map((p, i) => (
                            <TableRow key={p.id} sx={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                              <TableCell>{formatDate(p.purchaseDate || p.createdAt)}</TableCell>
                              <TableCell>{p.planName || '—'}</TableCell>
                              <TableCell align="center">{p.sessionsAdded || 0}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>${(p.amountPaid || 0).toFixed(2)}</TableCell>
                              <TableCell align="center">
                                <Chip label={p.paymentStatus} size="small"
                                  sx={{ backgroundColor: STATUS_COLORS[p.paymentStatus]?.bg || '#f0f0f0', color: STATUS_COLORS[p.paymentStatus]?.color || '#555', fontWeight: 600, textTransform: 'capitalize' }} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {linkedAthletes.filter(a => childHistory[a.id]).map(a => {
                    const h = childHistory[a.id];
                    const st = childPlanStatus(a, h.planName);
                    return (
                      <Box key={a.id} sx={{ mt: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F' }}>{childName(a)}</Typography>
                          <Chip size="small" label={st.label}
                            sx={{ backgroundColor: st.bg, color: st.color, fontWeight: 600 }} />
                        </Box>
                        {st.note && (
                          <Typography variant="body2" sx={{ color: '#555', mb: 1 }}>{st.note}</Typography>
                        )}
                        {h.purchases.length === 0 ? (
                          <Typography variant="body2" sx={{ color: '#aaa' }}>No purchases recorded yet.</Typography>
                        ) : (
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ backgroundColor: '#1E3A5F' }}>
                                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Plan</TableCell>
                                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Sessions</TableCell>
                                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Amount</TableCell>
                                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Status</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {h.purchases.map((p, i) => (
                                  <TableRow key={p.id} sx={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                                    <TableCell>{formatDate(p.purchaseDate || p.createdAt)}</TableCell>
                                    <TableCell>{p.planName || '—'}</TableCell>
                                    <TableCell align="center">{p.sessionsAdded ?? p.sessions ?? 0}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>${Number(p.amountPaid ?? p.amount ?? 0).toFixed(2)}</TableCell>
                                    <TableCell align="center">
                                      {p.paymentStatus && (
                                        <Chip label={p.paymentStatus} size="small"
                                          sx={{ backgroundColor: STATUS_COLORS[p.paymentStatus]?.bg || '#f0f0f0', color: STATUS_COLORS[p.paymentStatus]?.color || '#555', fontWeight: 600, textTransform: 'capitalize' }} />
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Box>
                    );
                  })}
                  {userPurchases.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>
                        Membership Payments
                        <Typography component="span" variant="caption" sx={{ color: '#888', fontWeight: 400, ml: 1 }}>
                          (account-level — renewals bill in Stripe without records here)
                        </Typography>
                      </Typography>
                      {userPurchases
                        .sort((a, b) => ((b.purchaseDate?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0) - (a.purchaseDate?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0)))
                        .slice(0, 10)
                        .map(p => (
                          <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #eee' }}>
                            <Typography variant="body2">{p.tierName || p.planName || p.description || p.purchaseType || 'Payment'}</Typography>
                            <Typography variant="body2" sx={{ color: '#555', whiteSpace: 'nowrap', ml: 2 }}>
                              {p.amount != null ? `$${Number(p.amount).toFixed(2)}` : (p.amountPaid != null ? `$${Number(p.amountPaid).toFixed(2)}` : '')}
                              {' '}{formatDate(p.purchaseDate || p.createdAt || p.timestamp)}
                            </Typography>
                          </Box>
                        ))}
                    </Box>
                  )}
                </TabPanel>

                {/* Coaching */}
                {hasCoach && (
                  <TabPanel value={at} index="coaching">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F' }}>Coaching Details</Typography>
                      {isAdmin && userData.email && (
                        <Button
                          size="small" variant="outlined" startIcon={<EmailIcon />}
                          onClick={handleSendInvite} disabled={inviteSending}
                          sx={{ color: '#1E3A5F', borderColor: '#1E3A5F' }}
                        >
                          {inviteSending ? 'Sending...' : 'Send Invite'}
                        </Button>
                      )}
                    </Box>
                    {inviteSuccess && <Alert severity="success" sx={{ mb: 2 }}>{inviteSuccess}</Alert>}

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        {editing ? (
                          <TextField fullWidth label="Head Coach Rate ($/hour)" type="number"
                            value={form.headRatePerHour}
                            onChange={e => update('headRatePerHour', e.target.value)} />
                        ) : (
                          <>
                            <Typography variant="caption" sx={{ color: '#888' }}>Head Coach Rate</Typography>
                            <Typography variant="body1">${userData.headRatePerHour || 0}/hour</Typography>
                          </>
                        )}
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        {editing ? (
                          <TextField fullWidth label="Assistant Rate ($/hour)" type="number"
                            value={form.assistantRatePerHour}
                            onChange={e => update('assistantRatePerHour', e.target.value)} />
                        ) : (
                          <>
                            <Typography variant="caption" sx={{ color: '#888' }}>Assistant Rate</Typography>
                            <Typography variant="body1">${userData.assistantRatePerHour || 0}/hour</Typography>
                          </>
                        )}
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        {editing ? (
                          <TextField fullWidth label="Venmo Username"
                            value={form.venmoUsername}
                            onChange={e => update('venmoUsername', e.target.value)} />
                        ) : (
                          <>
                            <Typography variant="caption" sx={{ color: '#888' }}>Venmo</Typography>
                            <Typography variant="body1">{userData.venmoUsername || '—'}</Typography>
                          </>
                        )}
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" sx={{ color: '#888' }}>Sessions Coached</Typography>
                        <Typography variant="body1">
                          {userData.totalSessionsLed || 0} head · {userData.totalAssistSessions || 0} assist
                        </Typography>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1.5 }}>
                      Recent Classes ({recentClasses.length})
                    </Typography>
                    {recentClasses.length === 0 ? (
                      <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic' }}>
                        No classes assigned yet.
                      </Typography>
                    ) : (
                      <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f4f6f9' }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {recentClasses.map(c => (
                              <TableRow key={c.id} hover>
                                <TableCell><Typography variant="caption">{c.date}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{c.startTime}–{c.endTime}</Typography></TableCell>
                                <TableCell>
                                  <Typography variant="caption">
                                    {c.sessionType || '—'} ({classAttendees[c.id] || 0})
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip label={c.role} size="small"
                                    sx={{ fontSize: '0.7rem', backgroundColor: c.role === 'Head Coach' ? '#DEEAF1' : '#F4F6F7' }} />
                                </TableCell>
                                <TableCell>
                                  {c.status === 'canceled'
                                    ? <Chip label="Canceled" size="small" color="error" sx={{ fontSize: '0.7rem' }} />
                                    : <Chip label="Scheduled" size="small" sx={{ fontSize: '0.7rem', backgroundColor: '#E9F7EF' }} />}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </TabPanel>
                )}

                {/* Linked Athletes */}
                {hasAthletes && (
                  <TabPanel value={at} index="athletes">
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Linked Athletes</Typography>
                    {linkedAthletes.map(athlete => (
                      <Box key={athlete.id}
                        onClick={() => navigate(`/athletes/${athlete.id}`)}
                        sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, p: 1.5, borderRadius: 2, backgroundColor: '#f9f9f9', cursor: 'pointer', '&:hover': { backgroundColor: '#DEEAF1' } }}>
                        <Avatar src={athlete.photoURL || ''} sx={{ width: 40, height: 40, backgroundColor: '#2E6DA4', fontSize: '0.9rem' }}>
                          {!athlete.photoURL && (athlete.firstName?.charAt(0) || '') + (athlete.lastName?.charAt(0) || '')}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>{athlete.firstName} {athlete.lastName}</Typography>
                          <Typography variant="body2" sx={{ color: '#888', textTransform: 'capitalize' }}>
                            {athlete.athleteType} • {athlete.skillGroup || 'No skill group'}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: (athlete.sessionBalance || 0) > 2 ? '#27AE60' : (athlete.sessionBalance || 0) > 0 ? '#E67E22' : '#E74C3C' }}>
                          {athlete.sessionBalance || 0} sessions
                        </Typography>
                      </Box>
                    ))}
                  </TabPanel>
                )}

                {/* Tournaments */}
                <TabPanel value={at} index="tournaments">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 2 }}>Tournament History</Typography>
                  {tournamentHistoryLoading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
                  ) : tournamentHistory.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#aaa' }}>No tournament results yet.</Typography>
                  ) : (
                  <>
                    {/* Summary stats */}
                    {(() => {
                      const totalEarned = tournamentHistory.reduce((sum, t) => sum + (t.earned_points || 0), 0);
                      const validPlaces = tournamentHistory.map(t => t.place || 999).filter(p => p > 0 && p < 999);
                      const bestPlace = validPlaces.length ? Math.min(...validPlaces) : null;
                      const bestEntry = bestPlace ? tournamentHistory.find(t => t.place === bestPlace) : null;
                      return (
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2, textAlign: 'center', bgcolor: '#0D1E35', color: 'white' }}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 1, textTransform: 'uppercase' }}>
                                Tournaments
                              </Typography>
                              <Typography variant="h4" sx={{ fontWeight: 700, color: '#C9A84C' }}>
                                {tournamentHistory.length}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2, textAlign: 'center', bgcolor: '#0D1E35', color: 'white' }}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 1, textTransform: 'uppercase' }}>
                                Total Earned Points
                              </Typography>
                              <Typography variant="h4" sx={{ fontWeight: 700, color: '#C9A84C' }}>
                                {totalEarned}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2, textAlign: 'center', bgcolor: '#0D1E35', color: 'white' }}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 1, textTransform: 'uppercase' }}>
                                Best Finish
                              </Typography>
                              {bestEntry ? (
                                <>
                                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#C9A84C' }}>
                                    {bestPlace}{bestPlace === 1 ? 'st' : bestPlace === 2 ? 'nd' : bestPlace === 3 ? 'rd' : 'th'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                    {bestEntry.division} · {bestEntry.tournament_name}
                                  </Typography>
                                </>
                              ) : (
                                <Typography variant="h4" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.4)' }}>—</Typography>
                              )}
                            </Paper>
                          </Grid>
                        </Grid>
                      );
                    })()}

                    {/* Results table */}
                    <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f4f6f9' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Tournament</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Division</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Partner</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Place</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Record</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Points</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tournamentHistory.map((t, i) => {
                            const place = t.place || 0;
                            const medalColor = place === 1 ? '#FFD700' : place === 2 ? '#C0C0C0' : place === 3 ? '#CD7F32' : null;
                            return (
                              <TableRow key={i} hover sx={{ cursor: 'pointer' }}
                                onClick={() => window.open(`/tournament/${t.tournament_id}/results`, '_blank')}>
                                <TableCell><Typography variant="caption">{t.date}</Typography></TableCell>
                                <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{t.tournament_name}</Typography></TableCell>
                                <TableCell><Chip label={t.division} size="small" sx={{ fontSize: '0.7rem' }} /></TableCell>
                                <TableCell><Typography variant="caption" color="text.secondary">{t.partner_name || '—'}</Typography></TableCell>
                                <TableCell sx={{ textAlign: 'center' }}>
                                  <Box sx={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: 28, height: 28, borderRadius: '50%',
                                    backgroundColor: medalColor || '#f0f2f5',
                                    color: medalColor ? '#0D1E35' : '#666',
                                    fontWeight: 700, fontSize: '0.85rem',
                                  }}>
                                    {place || '—'}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ textAlign: 'center', fontFamily: 'monospace' }}>
                                  {t.wins}–{t.losses}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'center', fontWeight: 700, color: '#C9A84C' }}>
                                  {t.earned_points || 0}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', textAlign: 'right' }}>
                      Click a row to view that tournament's full results.
                    </Typography>
                  </>
                  )}
                </TabPanel>

              </Box>
            </Paper>
          );
        })()}

      </Box>

      {/* Balance Update Dialog (admin) */}
      <Dialog open={clinicDialog} onClose={() => !savingClinic && setClinicDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Clinic Credits</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
            Current: <strong>{userData.clinicCredits ?? 0}</strong> credit{(userData.clinicCredits ?? 0) === 1 ? '' : 's'}
          </Typography>
          <TextField autoFocus fullWidth type="number" label="New credit total" value={newClinic}
            onChange={e => setNewClinic(e.target.value)} inputProps={{ min: 0 }} />
        </DialogContent>
        <DialogActions>
          <Button disabled={savingClinic} onClick={() => setClinicDialog(false)}>Cancel</Button>
          <Button variant="contained" disabled={savingClinic}
            onClick={async () => {
              const val = parseInt(newClinic, 10);
              if (isNaN(val) || val < 0) return;
              setSavingClinic(true);
              try {
                await updateDoc(doc(db, 'users', userData.id || userId), { clinicCredits: val });
                setUserData(prev => ({ ...prev, clinicCredits: val }));
                setClinicDialog(false);
              } catch (e) { alert('Update failed: ' + e.message); }
              finally { setSavingClinic(false); }
            }}>
            {savingClinic ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={balanceDialog} onClose={() => setBalanceDialog(false)}>
        <DialogTitle>Update Session Balance</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
            Current balance: <strong>{athleteData?.sessionBalance ?? 0}</strong> sessions
          </Typography>
          <TextField fullWidth label="New Session Balance" type="number"
            value={newBalance} onChange={e => setNewBalance(e.target.value)} autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBalanceDialog(false)}>Cancel</Button>
          <Button onClick={handleBalanceUpdate} variant="contained" disabled={savingBalance} sx={{ backgroundColor: '#27AE60' }}>
            {savingBalance ? 'Saving...' : 'Update Balance'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default UserProfile;
